import { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { RefreshCw } from 'lucide-react';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { useBankEntries } from '../../hooks/react-query/useGeneralLedger';
import { BankIncomingSection } from './BankIncomingSection';
import { Badge } from '../ui/badge';
import { BANK_TRANSACTION_TYPE_LABELS } from '@/types/bank-reconciliation';
import type { FundBankAccountTransaction } from '@/types/fund-bank-transactions';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

const describeCounterparty = (entry: FundBankAccountTransaction) =>
  entry.counterparty_name || entry.counterparty_reference || '-';

interface BankEntriesContentProps {
  isLoading: boolean;
  entries: FundBankAccountTransaction[];
}

function BankEntriesContent({ isLoading, entries }: BankEntriesContentProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
        Loading bank entries...
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No bank transactions found for this fund.
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[120px]">DATE</TableHead>
            <TableHead className="w-[200px]">ACCOUNT</TableHead>
            <TableHead className="w-[200px]">COUNTERPARTY</TableHead>
            <TableHead>DESCRIPTION</TableHead>
            <TableHead className="w-[170px]">TYPE</TableHead>
            <TableHead className="text-right w-[140px]">DEBIT</TableHead>
            <TableHead className="text-right w-[140px]">CREDIT</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell className="align-top">
                <span className="text-foreground">
                  {formatDate(entry.value_date ?? entry.execution_date)}
                </span>
              </TableCell>
              <TableCell>
                <div>
                  <div className="font-semibold text-foreground">
                    {entry.account_name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {entry.bank_name}
                  </div>
                </div>
              </TableCell>
              <TableCell className="align-top">
                {describeCounterparty(entry)}
              </TableCell>
              <TableCell className="align-top">
                <div>
                  <div className="font-medium text-foreground">
                    {entry.remittance_information || '-'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {entry.source}
                  </div>
                </div>
              </TableCell>
              <TableCell className="align-top">
                {entry.fund_transaction_type ? (
                  <Badge variant="secondary">
                    {entry.fund_transaction_type}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    {BANK_TRANSACTION_TYPE_LABELS[entry.bank_transaction_type]}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right font-medium">
                {entry.amount < 0
                  ? formatCurrency(Math.abs(entry.amount), {
                      currency: entry.currency,
                    })
                  : '-'}
              </TableCell>
              <TableCell className="text-right font-medium">
                {entry.amount > 0
                  ? formatCurrency(entry.amount, {
                      currency: entry.currency,
                    })
                  : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface BankEntriesTabProps {
  fundId?: string;
}

export function BankEntriesTab({ fundId }: BankEntriesTabProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const bankEntriesQuery = useBankEntries(fundId, {
    page: currentPage,
    page_size: pageSize,
  });

  const entries = bankEntriesQuery.data?.items ?? [];
  const totalCount = bankEntriesQuery.data?.total_count ?? 0;
  const isLoading = bankEntriesQuery.isLoading;

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const paginationPages = useMemo(() => {
    const totalPages = Math.ceil(totalCount / pageSize);
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(
        1,
        '...',
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        '...',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        '...',
        totalPages
      );
    }
    return pages;
  }, [totalCount, pageSize, currentPage]);

  return (
    <Card>
      <CardContent className="pt-6">
        <BankIncomingSection fundId={fundId} />
        <BankEntriesContent isLoading={isLoading} entries={entries} />

        {totalCount > pageSize && (
          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-4">
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1}-
                {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Per page:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={handlePageSizeChange}
                >
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {paginationPages.map((page, idx) =>
                page === '...' ? (
                  <span
                    key={
                      paginationPages[idx - 1] === 1
                        ? 'ellipsis-start'
                        : 'ellipsis-end'
                    }
                    className="px-2 text-muted-foreground"
                  >
                    ...
                  </span>
                ) : (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="sm"
                    className="min-w-[36px]"
                    onClick={() => setCurrentPage(page as number)}
                  >
                    {page}
                  </Button>
                )
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage * pageSize >= totalCount}
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize))}
                disabled={currentPage * pageSize >= totalCount}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
